import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { socket } from '../services/socket';
import PacmanBoard from './PacmanBoard';
import PlayersList from './PlayersList';
import '../styles/GameScreen.css';

function GameScreen({ playerName, playerId }) {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const [gameState, setGameState] = useState(null);
    const [roomName, setRoomName] = useState('');
    const [isHost, setIsHost] = useState(false);
    const [countdown, setCountdown] = useState(null);

    // Track if component is mounted
    const isMounted = useRef(true);

    useEffect(() => {
        // Set up when component mounts
        isMounted.current = true;

        // Clean up when component unmounts
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!roomId || !playerName) {
            navigate('/');
            return;
        }

        // Set up event listeners
        socket.on('roomInfo', (info) => {
            if (isMounted.current) {
                setRoomName(info.name);
                setIsHost(info.hostId === socket.id);
            }
        });

        socket.on('gameState', (state) => {
            if (isMounted.current) {
                setGameState(state);
            }
        });

        socket.on('countdown', (count) => {
            if (isMounted.current) {
                setCountdown(count);
            }
        });

        socket.on('roomClosed', () => {
            if (isMounted.current) {
                alert('The room has been closed by the host');
                navigate('/');
            }
        });

        // Join the room
        socket.emit('joinRoom', {
            roomId,
            playerName
        });

        // Clean up event listeners
        return () => {
            socket.off('roomInfo');
            socket.off('gameState');
            socket.off('countdown');
            socket.off('roomClosed');
            socket.emit('leaveRoom', roomId);
        };
    }, [roomId, playerName, navigate]);

    const startGame = () => {
        if (isHost && roomId) {
            socket.emit('startGame', roomId);
        }
    };

    const leaveGame = () => {
        navigate('/');
    };

    const handleKeyDown = (e) => {
        if (!gameState?.gameStarted || !roomId) return;

        let direction = null;

        switch (e.key) {
            case 'ArrowUp':
                direction = 'up';
                break;
            case 'ArrowDown':
                direction = 'down';
                break;
            case 'ArrowLeft':
                direction = 'left';
                break;
            case 'ArrowRight':
                direction = 'right';
                break;
            default:
                return;
        }

        if (direction) {
            socket.emit('playerMove', {
                roomId,
                direction
            });
        }
    };

    return (
        <div
            className="game-screen"
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            <div className="game-header">
                <h2>Room: {roomName}</h2>
                <button className="leave-button" onClick={leaveGame}>
                    Leave Game
                </button>
            </div>

            {countdown !== null && (
                <div className="countdown">
                    Game starting in: {countdown}
                </div>
            )}

            {gameState ? (
                <div className="game-container">
                    <PlayersList players={gameState.players} currentPlayerId={playerId} />

                    <div className="game-board-container">
                        {!gameState.gameStarted && !gameState.gameOver && isHost && (
                            <button
                                className="start-button"
                                onClick={startGame}
                                disabled={gameState.players.length < 1}
                            >
                                Start Game
                            </button>
                        )}

                        {gameState.gameOver && (
                            <div className="game-over">
                                <h2>Game Over!</h2>
                                {gameState.winner && (
                                    <p>Winner: {gameState.winner}</p>
                                )}
                                {isHost && (
                                    <button
                                        className="restart-button"
                                        onClick={startGame}
                                    >
                                        Play Again
                                    </button>
                                )}
                            </div>
                        )}

                        <PacmanBoard
                            gameState={gameState}
                            playerId={playerId}
                        />
                    </div>
                </div>
            ) : (
                <div className="loading">Loading game...</div>
            )}

            <div className="controls-info">
                <h3>Controls</h3>
                <p>Use arrow keys to move your Pac-Man</p>
            </div>
        </div>
    );
}

export default GameScreen;
